import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import type { EvaluationQuestion, EvaluationQuestionOption, EvaluationQuestionType } from "../evaluationFormsApi";
import {
  archiveEvaluationFormQuestionApi,
  createEvaluationFormQuestionApi,
  createEvaluationQuestionOptionApi,
  deleteEvaluationQuestionOptionApi,
  updateEvaluationFormQuestionApi,
  updateEvaluationQuestionOptionApi,
} from "../evaluationFormQuestionsApi";

export const QUESTION_TYPE_OPTIONS: { value: EvaluationQuestionType; label: string }[] = [
  { value: "BOOLEAN", label: "Sim ou Não" },
  { value: "SINGLE_CHOICE", label: "Escolha única" },
  { value: "MULTI_CHOICE", label: "Múltipla escolha" },
  { value: "SCALE", label: "Escala" },
  { value: "TEXT", label: "Texto livre" },
];

export const isChoiceType = (t: EvaluationQuestionType) => t === "SINGLE_CHOICE" || t === "MULTI_CHOICE";
const isScaleType = (t: EvaluationQuestionType) => t === "SCALE";

type QuestionPanelProps = {
  formId: string;
  /** `null` = pergunta nova. */
  question: EvaluationQuestion | null;
  /** Ordem sugerida para pergunta nova (a próxima da lista). */
  nextOrder: number;
  /** Dimensões já usadas no formulário, para sugerir no campo. */
  dimensionSuggestions: string[];
  /** Formulário arquivado: tudo em modo leitura (regra que já valia no modal antigo). */
  readOnly: boolean;
  onClose: () => void;
  /** Recarrega o formulário; devolve a pergunta atualizada para o painel continuar aberto nela. */
  onChanged: (questionId?: string) => Promise<EvaluationQuestion | null>;
  onArchived: () => void;
};

const parseNum = (s: string) => (s.trim() === "" ? undefined : Number(s));

/**
 * Painel lateral de pergunta: cria ou edita a pergunta e, nas de escolha, as
 * opções. Regras portadas dos modais antigos (`UpdateEvaluationFormModal` e
 * `ManageEvaluationFormQuestionsModal`): enunciado obrigatório, mín./máx. só
 * na escala e numéricos, mín. ≤ máx., opção nova vai para o fim.
 */
const QuestionPanel: React.FC<QuestionPanelProps> = ({
  formId,
  question: initialQuestion,
  nextOrder,
  dimensionSuggestions,
  readOnly,
  onClose,
  onChanged,
  onArchived,
}) => {
  const [question, setQuestion] = useState<EvaluationQuestion | null>(initialQuestion);
  const isEdit = Boolean(question);

  const [type, setType] = useState<EvaluationQuestionType>(question?.type ?? "BOOLEAN");
  const [prompt, setPrompt] = useState(question?.prompt ?? "");
  const [required, setRequired] = useState(question?.required ?? false);
  const [order, setOrder] = useState(String(question?.order ?? nextOrder));
  const [dimension, setDimension] = useState(question?.dimension ?? "");
  const [minValue, setMinValue] = useState(question?.minValue == null ? "" : String(question.minValue));
  const [maxValue, setMaxValue] = useState(question?.maxValue == null ? "" : String(question.maxValue));

  const [newOptLabel, setNewOptLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const promptRef = useRef<HTMLTextAreaElement>(null);

  const isArchivedQuestion = Boolean(question?.archivedAt);
  const locked = readOnly || isArchivedQuestion || busy;
  const options = useMemo(
    () => (question?.options ?? []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [question],
  );

  useEffect(() => {
    promptRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, busy]);

  const run = async (task: () => Promise<void>) => {
    try {
      setBusy(true);
      await task();
    } finally {
      setBusy(false);
    }
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (locked) return;

    const trimmed = prompt.trim();
    if (!trimmed) {
      toast.error("Informe o enunciado da pergunta.");
      return;
    }
    const orderNum = Number(order);
    if (!Number.isFinite(orderNum) || orderNum < 1) {
      toast.error("A ordem precisa ser um número a partir de 1.");
      return;
    }
    const min = isScaleType(type) ? parseNum(minValue) : undefined;
    const max = isScaleType(type) ? parseNum(maxValue) : undefined;
    if ((min !== undefined && Number.isNaN(min)) || (max !== undefined && Number.isNaN(max))) {
      toast.error("Valor mínimo e máximo precisam ser números.");
      return;
    }
    if (min !== undefined && max !== undefined && min > max) {
      toast.error("O valor mínimo não pode ser maior que o máximo.");
      return;
    }

    const dto = {
      type,
      prompt: trimmed,
      required,
      order: orderNum,
      dimension: dimension.trim() || undefined,
      minValue: min,
      maxValue: max,
    };

    run(async () => {
      try {
        if (question) {
          await updateEvaluationFormQuestionApi(formId, question.id, dto);
          toast.success("Pergunta salva.");
          const fresh = await onChanged(question.id);
          if (fresh) setQuestion(fresh);
        } else {
          const created = await createEvaluationFormQuestionApi(formId, dto);
          toast.success(
            isChoiceType(type) ? "Pergunta criada. Agora cadastre as opções." : "Pergunta criada.",
          );
          const fresh = await onChanged(created.id);
          // Escolha precisa de opções: o painel continua aberto, já em modo edição.
          if (isChoiceType(type)) setQuestion(fresh ?? (created as unknown as EvaluationQuestion));
          else onClose();
        }
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Erro ao salvar pergunta.");
      }
    });
  };

  const handleArchive = () =>
    run(async () => {
      if (!question) return;
      try {
        await archiveEvaluationFormQuestionApi(formId, question.id);
        toast.success("Pergunta arquivada.");
        onArchived();
      } catch {
        toast.error("Erro ao arquivar pergunta.");
      }
    });

  const handleAddOption = () =>
    run(async () => {
      if (!question || !newOptLabel.trim()) return;
      const nextOptOrder = options.reduce((m, o) => Math.max(m, o.order ?? 0), 0) + 1;
      try {
        await createEvaluationQuestionOptionApi(formId, question.id, {
          label: newOptLabel.trim(),
          order: nextOptOrder,
        });
        setNewOptLabel("");
        const fresh = await onChanged(question.id);
        if (fresh) setQuestion(fresh);
      } catch {
        toast.error("Erro ao criar opção.");
      }
    });

  const handleSaveOption = (opt: EvaluationQuestionOption, next: { label: string; value?: string; order: number }) =>
    run(async () => {
      if (!question) return;
      try {
        await updateEvaluationQuestionOptionApi(formId, question.id, opt.id, next);
        toast.success("Opção atualizada.");
        const fresh = await onChanged(question.id);
        if (fresh) setQuestion(fresh);
      } catch {
        toast.error("Erro ao atualizar opção.");
      }
    });

  const handleDeleteOption = (opt: EvaluationQuestionOption) =>
    run(async () => {
      if (!question) return;
      try {
        await deleteEvaluationQuestionOptionApi(formId, question.id, opt.id);
        toast.success("Opção removida.");
        const fresh = await onChanged(question.id);
        if (fresh) setQuestion(fresh);
      } catch {
        toast.error("Erro ao remover opção.");
      }
    });

  const showOptions = isEdit && isChoiceType(question?.type ?? type);

  return (
    <div className="qPanel__overlay" role="presentation" onClick={() => !busy && onClose()}>
      <aside
        className="qPanel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="qPanel-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="qPanel__header">
          <div className="qPanel__heading">
            <span className="qPanel__eyebrow">Formulário de perguntas</span>
            <h2 id="qPanel-title" className="qPanel__title">
              {isEdit ? `Pergunta ${question?.order}` : "Nova pergunta"}
            </h2>
            <p className="qPanel__hint">
              {readOnly
                ? "Formulário arquivado: reative para editar."
                : isArchivedQuestion
                  ? "Pergunta arquivada: não aparece mais nas avaliações."
                  : "Só perguntas do tipo Sim ou Não entram no cálculo da nota."}
            </p>
          </div>
          <button type="button" className="ecoBtn ecoBtn--icon" onClick={onClose} aria-label="Fechar" disabled={busy}>
            <EcoIcon name="x" />
          </button>
        </header>

        <div className="qPanel__body">
          <form id="qPanel-form" className="qPanel__form" onSubmit={handleSave}>
            <div className="ecoField">
              <label htmlFor="q-prompt">Enunciado</label>
              <textarea
                ref={promptRef}
                id="q-prompt"
                className="ecoInput qPanel__textarea"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Ex.: Validade dos produtos conferida?"
                disabled={locked}
                rows={3}
              />
            </div>

            <div className="qPanel__grid">
              <div className="ecoField">
                <label htmlFor="q-type">Tipo</label>
                <select
                  id="q-type"
                  className="ecoSelect"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  disabled={locked}
                >
                  {QUESTION_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                  {!QUESTION_TYPE_OPTIONS.some((o) => o.value === type) ? <option value={type}>{type}</option> : null}
                </select>
              </div>

              <div className="ecoField">
                <label htmlFor="q-order">Ordem</label>
                <input
                  id="q-order"
                  className="ecoInput"
                  type="number"
                  min={1}
                  value={order}
                  onChange={(e) => setOrder(e.target.value)}
                  disabled={locked}
                />
              </div>
            </div>

            {isScaleType(type) ? (
              <div className="qPanel__grid">
                <div className="ecoField">
                  <label htmlFor="q-min">
                    Valor mínimo <span className="ecoLabel__optional">(opcional)</span>
                  </label>
                  <input id="q-min" className="ecoInput" type="number" value={minValue} onChange={(e) => setMinValue(e.target.value)} disabled={locked} />
                </div>
                <div className="ecoField">
                  <label htmlFor="q-max">
                    Valor máximo <span className="ecoLabel__optional">(opcional)</span>
                  </label>
                  <input id="q-max" className="ecoInput" type="number" value={maxValue} onChange={(e) => setMaxValue(e.target.value)} disabled={locked} />
                </div>
              </div>
            ) : null}

            <div className="ecoField">
              <label htmlFor="q-dimension">
                Dimensão <span className="ecoLabel__optional">(opcional, agrupa perguntas nos painéis)</span>
              </label>
              <input
                id="q-dimension"
                className="ecoInput"
                list="q-dimension-list"
                value={dimension}
                onChange={(e) => setDimension(e.target.value)}
                disabled={locked}
                autoComplete="off"
              />
              <datalist id="q-dimension-list">
                {dimensionSuggestions.map((d) => (
                  <option key={d} value={d} />
                ))}
              </datalist>
            </div>

            <label className="qPanel__check">
              <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} disabled={locked} />
              <span>Resposta obrigatória</span>
            </label>
          </form>

          {showOptions ? (
            <section className="qPanel__options" aria-labelledby="qPanel-options">
              <h3 id="qPanel-options" className="qPanel__optionsTitle">
                Opções de resposta
              </h3>
              {options.length === 0 ? (
                <p className="qPanel__warn">Pergunta de escolha precisa de pelo menos uma opção.</p>
              ) : (
                <ul className="qPanel__optionList">
                  {options.map((opt) => (
                    <OptionRow
                      key={`${opt.id}-${opt.label}-${opt.order}`}
                      option={opt}
                      disabled={locked}
                      onSave={(next) => handleSaveOption(opt, next)}
                      onDelete={() => handleDeleteOption(opt)}
                    />
                  ))}
                </ul>
              )}
              {!locked ? (
                <div className="qPanel__newOption">
                  <label htmlFor="q-new-option" className="ecoVisuallyHidden">
                    Nova opção
                  </label>
                  <input
                    id="q-new-option"
                    className="ecoInput ecoInput--sm"
                    placeholder="Nova opção"
                    value={newOptLabel}
                    onChange={(e) => setNewOptLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddOption();
                      }
                    }}
                  />
                  <button type="button" className="ecoBtn ecoBtn--sm" onClick={handleAddOption} disabled={!newOptLabel.trim()}>
                    <EcoIcon name="plus" size={16} />
                    Adicionar
                  </button>
                </div>
              ) : null}
            </section>
          ) : null}

          {isEdit && !readOnly && !isArchivedQuestion ? (
            <div className="qPanel__danger">
              {confirmArchive ? (
                <>
                  <span>Arquivar esta pergunta? Ela sai das próximas avaliações; as respostas já dadas ficam.</span>
                  <div className="qPanel__dangerActions">
                    <button type="button" className="ecoBtn ecoBtn--sm" onClick={() => setConfirmArchive(false)} disabled={busy}>
                      Cancelar
                    </button>
                    <button type="button" className="ecoBtn ecoBtn--sm ecoBtn--dangerSolid" onClick={handleArchive} disabled={busy}>
                      Arquivar pergunta
                    </button>
                  </div>
                </>
              ) : (
                <button type="button" className="ecoBtn ecoBtn--sm ecoBtn--danger" onClick={() => setConfirmArchive(true)} disabled={busy}>
                  Arquivar pergunta
                </button>
              )}
            </div>
          ) : null}
        </div>

        <footer className="qPanel__footer">
          <button type="button" className="ecoBtn" onClick={onClose} disabled={busy}>
            {isEdit ? "Fechar" : "Cancelar"}
          </button>
          {!readOnly && !isArchivedQuestion ? (
            <button type="submit" form="qPanel-form" className="ecoBtn ecoBtn--primary" disabled={busy || !prompt.trim()}>
              {busy ? "Salvando..." : isEdit ? "Salvar pergunta" : "Criar pergunta"}
            </button>
          ) : null}
        </footer>
      </aside>
    </div>
  );
};

const OptionRow: React.FC<{
  option: EvaluationQuestionOption;
  disabled: boolean;
  onSave: (next: { label: string; value?: string; order: number }) => void;
  onDelete: () => void;
}> = ({ option, disabled, onSave, onDelete }) => {
  const [label, setLabel] = useState(option.label);
  const [order, setOrder] = useState(String(option.order ?? 0));
  const dirty = label.trim() !== option.label || Number(order) !== Number(option.order ?? 0);

  return (
    <li className="qPanel__optionRow">
      <label className="ecoVisuallyHidden" htmlFor={`opt-order-${option.id}`}>
        Ordem da opção
      </label>
      <input
        id={`opt-order-${option.id}`}
        className="ecoInput ecoInput--sm qPanel__optOrder"
        type="number"
        min={1}
        value={order}
        onChange={(e) => setOrder(e.target.value)}
        disabled={disabled}
      />
      <label className="ecoVisuallyHidden" htmlFor={`opt-label-${option.id}`}>
        Texto da opção
      </label>
      <input
        id={`opt-label-${option.id}`}
        className="ecoInput ecoInput--sm"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        disabled={disabled}
      />
      {dirty ? (
        <button
          type="button"
          className="ecoBtn ecoBtn--sm"
          onClick={() => onSave({ label: label.trim(), value: option.value ?? undefined, order: Number(order) || 1 })}
          disabled={disabled || !label.trim()}
        >
          Salvar
        </button>
      ) : null}
      <button
        type="button"
        className="ecoBtn ecoBtn--icon qPanel__optDelete"
        onClick={onDelete}
        disabled={disabled}
        aria-label={`Remover opção ${option.label}`}
      >
        <EcoIcon name="x" size={18} />
      </button>
    </li>
  );
};

export default QuestionPanel;
